import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, Users, Edit2, Trash2, Eye, Filter } from 'lucide-react';

export default function Segments() {
  const [segments, setSegments] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', filters: {} });
  const [preview, setPreview] = useState(null);
  const [previewCustomers, setPreviewCustomers] = useState([]);

  const fetchSegments = async () => {
    const res = await api.get('/tenant/segments');
    setSegments(res.data.segments || []);
  };
  useEffect(() => { fetchSegments(); }, []);

  const openNew = () => { setEditing(null); setForm({ name: '', description: '', filters: {} }); setShowForm(true); };
  const openEdit = (s) => { setEditing(s); setForm({ name: s.name, description: s.description || '', filters: s.filters || {} }); setShowForm(true); };

  const handleSave = async () => {
    if (editing) {
      await api.put(`/tenant/segments/${editing.id}`, form);
    } else {
      await api.post('/tenant/segments', form);
    }
    setShowForm(false); fetchSegments();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Hapus segment ini?')) return;
    await api.delete(`/tenant/segments/${id}`);
    fetchSegments();
  };

  const handlePreview = async (seg) => {
    setPreview(seg);
    const res = await api.get(`/tenant/segments/${seg.id}/customers`);
    setPreviewCustomers(res.data.customers);
  };

  const setFilter = (key, val) => {
    const f = { ...form.filters };
    if (val === '' || val === undefined) { delete f[key]; } else { f[key] = isNaN(val) ? val : Number(val); }
    setForm({ ...form, filters: f });
  };

  const setFilterTags = (val) => {
    const f = { ...form.filters };
    const tags = val.split(',').map(t => t.trim()).filter(Boolean);
    if (tags.length) { f.tags = tags; } else { delete f.tags; }
    setForm({ ...form, filters: f });
  };

  const fmt = (v) => new Intl.NumberFormat('id-ID').format(v || 0);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>Segmentasi Pelanggan</h1>
        <Button onClick={openNew} className="bg-[#2D6CDF] hover:bg-[#1A5BC4]" size="sm">
          <Plus size={14} className="mr-1" /> Buat Segment
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {segments.map(s => (
          <Card key={s.id} className="border-slate-200">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">{s.name}</CardTitle>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" onClick={() => handlePreview(s)}><Eye size={14} /></Button>
                  <Button variant="ghost" size="icon" onClick={() => openEdit(s)}><Edit2 size={14} /></Button>
                  <Button variant="ghost" size="icon" className="text-red-500 hover:text-red-700" onClick={() => handleDelete(s.id)}><Trash2 size={14} /></Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {s.description && <p className="text-sm text-slate-500 mb-2">{s.description}</p>}
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="bg-blue-50 text-blue-700"><Users size={12} className="mr-1" /> {s.customer_count} pelanggan</Badge>
              </div>
              {s.filters && Object.keys(s.filters).length > 0 && (
                <div className="flex gap-1 flex-wrap mt-2">
                  {Object.entries(s.filters).map(([k, v]) => (
                    <Badge key={k} variant="outline" className="text-xs bg-slate-50">
                      <Filter size={10} className="mr-1" />{k}: {Array.isArray(v) ? v.join(', ') : v}
                    </Badge>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
        {!segments.length && (
          <Card className="col-span-full border-dashed border-slate-300">
            <CardContent className="py-12 text-center text-slate-400">
              Belum ada segment. Klik "Buat Segment" untuk memulai.
            </CardContent>
          </Card>
        )}
      </div>

      {/* Create/Edit Segment Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editing ? 'Edit' : 'Buat'} Segment</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Nama Segment</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="VIP Customers" /></div>
            <div><Label>Deskripsi</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Opsional" /></div>
            <div className="border rounded-lg p-4 bg-slate-50 space-y-3">
              <p className="font-medium text-sm text-slate-700 flex items-center gap-1"><Filter size={14} /> Filter Kriteria</p>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Min. Total Belanja</Label><Input type="number" value={form.filters.min_spent || ''} onChange={(e) => setFilter('min_spent', e.target.value)} placeholder="0" /></div>
                <div><Label className="text-xs">Max. Total Belanja</Label><Input type="number" value={form.filters.max_spent || ''} onChange={(e) => setFilter('max_spent', e.target.value)} placeholder="∞" /></div>
                <div><Label className="text-xs">Min. Jumlah Order</Label><Input type="number" value={form.filters.min_orders || ''} onChange={(e) => setFilter('min_orders', e.target.value)} placeholder="0" /></div>
                <div><Label className="text-xs">Max. Jumlah Order</Label><Input type="number" value={form.filters.max_orders || ''} onChange={(e) => setFilter('max_orders', e.target.value)} placeholder="∞" /></div>
              </div>
              <div><Label className="text-xs">Tags (pisah koma)</Label><Input value={(form.filters.tags || []).join(', ')} onChange={(e) => setFilterTags(e.target.value)} placeholder="vip, premium" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Daftar Setelah</Label><Input type="date" value={form.filters.registered_after || ''} onChange={(e) => setFilter('registered_after', e.target.value)} /></div>
                <div><Label className="text-xs">Daftar Sebelum</Label><Input type="date" value={form.filters.registered_before || ''} onChange={(e) => setFilter('registered_before', e.target.value)} /></div>
              </div>
            </div>
            <Button onClick={handleSave} className="w-full bg-[#2D6CDF] hover:bg-[#1A5BC4]">Simpan</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Preview Customers Dialog */}
      <Dialog open={!!preview} onOpenChange={() => setPreview(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Pelanggan di Segment: {preview?.name}</DialogTitle></DialogHeader>
          <div className="max-h-[400px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b"><th className="text-left py-2 px-3">Nama</th><th className="text-left py-2 px-3">Email</th><th className="text-right py-2 px-3">Total Spent</th></tr></thead>
              <tbody>
                {previewCustomers.map(c => (
                  <tr key={c.id} className="border-b border-slate-50">
                    <td className="py-2 px-3">{c.name}</td>
                    <td className="py-2 px-3 text-slate-500">{c.email}</td>
                    <td className="py-2 px-3 text-right">Rp {fmt(c.total_spent)}</td>
                  </tr>
                ))}
                {!previewCustomers.length && <tr><td colSpan={3} className="py-8 text-center text-slate-400">Tidak ada pelanggan yang cocok</td></tr>}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
